/* eslint-disable no-nested-ternary */
import React, { useMemo, useRef } from 'react';
import { get } from 'lodash';
import { FormattedMessage } from 'react-intl';
import format from 'date-fns/format';
import moment from 'moment-timezone';
import { Form as FinalForm } from 'react-final-form';

import { composeValidators, required } from '../../util/validators';
import { getBookingDates, hasLodgingForTransaction } from '../../util/transaction';
import { daysAdded, createTimeSlots } from '../../util/dates';
import { isFlexiblePricingEnabled } from '../../util/package';

import {
  Form,
  FieldDates,
  FieldPartySizeSelect,
  FlexiblePartySizeSelect,
  PrimaryButton,
  SecondaryButton,
  FieldLabel,
} from '..';

import css from './BookingChanges.module.css';

const BookingDetails = ({
  currentPackage,
  currentTransaction,
  timeSlots,
  setNewGuestSize,
  setNewBookingDates,
  onSubmit,
  inProgress,
  hasCustomerRequestedChanges,
  isProvider,
  onClose,
  onAccept,
  acceptChangesInProgress,
  onDecline,
  declineChangesInProgress,
  intl,
  pricingDetails,
  errorMessage,
  onDatePickerOpenChange,
  inlinePaymentElement,
  disableSubmit,
}) => {
  const packageMinParty = get(currentPackage, 'guests.min', 1);
  const packageMaxParty = get(currentPackage, 'guests.max', 1);
  const days = get(currentPackage, 'days', {});
  const guestSize = get(
    currentTransaction,
    'attributes.protectedData.packageLineItem.guestSize',
    1
  );

  const existingFlexiblePricing = get(
    currentTransaction,
    'attributes.protectedData.flexibleGuestSize',
    null
  );
  const originalStart = get(
    currentTransaction,
    'attributes.protectedData.bookingDates.start',
    null
  );
  const originalEnd = get(currentTransaction, 'attributes.protectedData.bookingDates.end', null);
  const originalLodgingDate = get(
    currentTransaction,
    'attributes.protectedData.bookingDates.startLodgingDate',
    null
  );

  let calendarInfoDays;
  const formFieldWrapperRef = useRef(null);

  if (days.min === days.max) {
    calendarInfoDays = days.min;
  } else if (days.max === 365) {
    calendarInfoDays = `${days.min}+`;
  } else {
    calendarInfoDays = `${days.min}-${days.max}`;
  }

  const { bookingStart, bookingEnd } = getBookingDates(currentTransaction);

  const numberOfDays = daysAdded(bookingStart, bookingEnd);

  const isSingleDay = numberOfDays === 1;
  let bookingDatesText;

  if (isSingleDay) {
    bookingDatesText = `${bookingStart.format('MMM DD, YYYY')}`;
  } else {
    bookingDatesText = `${bookingStart.format('MMM DD')} - ${bookingEnd.format('MMM DD, YYYY')}`;
  }

  const newTimeSlots = createTimeSlots(bookingStart, numberOfDays) || [];
  const updatedTimeSlots = (timeSlots || []).concat(newTimeSlots);

  const endDatePart = originalEnd.split('T')[0];
  const startDatePart = originalStart.split('T')[0];
  const lodgingDatePart = originalLodgingDate ? originalLodgingDate.split('T')[0] : null;

  const end = moment(endDatePart).set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
  const start = moment(startDatePart).set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
  const lodgingDate = lodgingDatePart
    ? moment(lodgingDatePart).set({
        hour: 12,
        minute: 0,
        second: 0,
        millisecond: 0,
      })
    : null;

  const initialValues = useMemo(() => {
    const partySizeValue = existingFlexiblePricing
      ? { partySize: guestSize, flexibleGuestSize: existingFlexiblePricing }
      : guestSize;

    return {
      partySize: partySizeValue,
      dates: {
        startDate: start.toDate(),
        endDate: end.toDate(),
        startLodgingDate: lodgingDate?.toDate(),
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let newPartySize;
  let newDates;

  if (hasCustomerRequestedChanges || isProvider) {
    const bookingChangesValues = get(
      currentTransaction,
      'attributes.protectedData.bookingChanges.newValues',
      {}
    );

    if (bookingChangesValues.partySize.flexibleGuestSize) {
      newPartySize = bookingChangesValues.partySize.partySize;
    } else {
      newPartySize = bookingChangesValues.partySize;
    }

    newDates = {
      startDate: new Date(JSON.parse(bookingChangesValues.dates.startDate)),
      endDate: new Date(JSON.parse(bookingChangesValues.dates.endDate)),
    };
  }

  const hasLodging = hasLodgingForTransaction(currentTransaction);

  return (
    <FinalForm
      keepDirtyOnReinitialize
      initialValues={initialValues}
      onSubmit={values => onSubmit(values, initialValues)}
      render={fieldRenderProps => {
        const { handleSubmit, form, values } = fieldRenderProps;

        let bookingDateText = <FormattedMessage id="CheckoutPage.datesFieldPlaceholderPackage" />;
        const dateValues =
          hasCustomerRequestedChanges || isProvider ? newDates : values && values.dates;

        const isSelectedSingleDay = daysAdded(dateValues.startDate, dateValues.endDate) === 1;

        if (dateValues) {
          bookingDateText = `${format(dateValues.startDate, 'MMM DD, YYYY')} - ${format(
            dateValues.endDate,
            'MMM DD, YYYY'
          )}`;
        }
        if (dateValues && isSelectedSingleDay) {
          bookingDateText = format(dateValues.startDate, 'MMM DD, YYYY');
        }

        // Handle both flexible pricing object and regular party size number
        const currentPartySize = values.partySize?.partySize || values.partySize;
        const initialPartySize = initialValues.partySize?.partySize || initialValues.partySize;

        const partySizeChanged =
          hasCustomerRequestedChanges || isProvider
            ? newPartySize !== initialPartySize
            : parseInt(currentPartySize, 10) !== parseInt(initialPartySize, 10);

        const datesChanged =
          moment(dateValues.startDate).format('YYYY-MM-DD') !==
            moment(initialValues.dates.startDate).format('YYYY-MM-DD') ||
          moment(dateValues.endDate).format('YYYY-MM-DD') !==
            moment(initialValues.dates.endDate).format('YYYY-MM-DD');

        const submitDisabled = !partySizeChanged && !datesChanged;
        const primaryActionDisabled = submitDisabled || disableSubmit;
        const renderedInlinePayment = inlinePaymentElement
          ? inlinePaymentElement({ values, initialValues, submitDisabled })
          : null;

        const formattedNewPartySize = newPartySize;
        const formattedPrice = JSON.parse(currentPackage.price);
        const basePriceAmount = formattedPrice.amount * 100;

        return (
          <Form onSubmit={handleSubmit} className={css.bookingDetails}>
            <div className={css.bookingDetailsContainer}>
              <div className={css.bookingDetailsHeader}>
                <FormattedMessage id="BookingChanges.bookingDetails" />
              </div>
            </div>

            <div className={css.formFieldWrapper}>
              {hasCustomerRequestedChanges || isProvider ? (
                <>
                  {partySizeChanged ? (
                    <>
                      <FieldLabel>
                        <FormattedMessage id="BookingChanges.newPartySize" />
                      </FieldLabel>

                      <div className={css.formFieldValue}>
                        <FormattedMessage
                          id="FieldPartySizeSelect.guestOption"
                          values={{
                            guests: formattedNewPartySize,
                          }}
                        />
                      </div>
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  {isFlexiblePricingEnabled(currentPackage.activity) && existingFlexiblePricing ? (
                    <FlexiblePartySizeSelect
                      name="partySize"
                      activity={currentPackage.activity}
                      minPartySize={guestSize || packageMinParty}
                      maxPartySize={packageMaxParty}
                      price={basePriceAmount}
                      packageFlexiblePricing={currentPackage.flexiblePricing}
                      packageInfo={currentPackage}
                      activityDays={numberOfDays}
                      minAdultHunter={existingFlexiblePricing?.adultHunter || 0}
                      minAdultNonHunter={existingFlexiblePricing?.adultNonHunter || 0}
                      minYouthHunter={existingFlexiblePricing?.youthHunter || 0}
                      minYouthNonHunter={existingFlexiblePricing?.youthNonHunter || 0}
                      validate={composeValidators(
                        required(intl.formatMessage({ id: 'PricingPage.partySizeRequired' }))
                      )}
                      onChange={partySizeData => {
                        setNewGuestSize(partySizeData);
                      }}
                      readOnlyInput
                      label={<FormattedMessage id="PricingPage.partySizeLabel" />}
                    />
                  ) : (
                    <FieldPartySizeSelect
                      id="partySizeBookingChange"
                      label={
                        partySizeChanged ? (
                          intl.formatMessage({ id: 'BookingChanges.newPartySize' })
                        ) : (
                          <FormattedMessage id="BookingChanges.originalPartySize" />
                        )
                      }
                      name="partySize"
                      minPartySize={guestSize || packageMinParty}
                      maxPartySize={packageMaxParty}
                      price={JSON.parse(currentPackage.price)}
                      parse={value => (value === '' ? null : Number(value))}
                      format={value => String(value)}
                      validate={composeValidators(
                        required(intl.formatMessage({ id: 'PricingPage.partySizeRequired' }))
                      )}
                      onChange={partySize => {
                        setNewGuestSize(partySize.target.value);
                        form.change('partySize', partySize.target.value);
                      }}
                    />
                  )}
                </>
              )}

              {partySizeChanged && (
                <div className={css.formFieldHelper}>
                  <FormattedMessage id="BookingChanges.original" />{' '}
                  <FormattedMessage
                    id="FieldPartySizeSelect.guestOption"
                    values={{
                      guests: guestSize,
                    }}
                  />
                </div>
              )}
            </div>

            <div className={css.formFieldWrapper} ref={formFieldWrapperRef}>
              {hasCustomerRequestedChanges || isProvider ? (
                <>
                  {datesChanged ? (
                    <>
                      <FieldLabel>
                        <FormattedMessage id="BookingChanges.newDates" />
                      </FieldLabel>

                      <div className={css.formFieldValue}>{bookingDateText}</div>
                    </>
                  ) : null}
                </>
              ) : (
                <FieldDates
                  fieldDateProps={{
                    endDateOffset:
                      days.min === days.max ? day => day.add(days.min - 1, 'days') : undefined,
                    minimumNights: days.min !== days.max && days.min - 1,
                    maximumNights: days.max > 0 && days.min !== days.max ? days.max - 1 : undefined,
                    calendarInfo: (
                      <FormattedMessage
                        id="PricingPage.datesFieldInfoPackageTitle"
                        values={{ days: calendarInfoDays }}
                      />
                    ),
                    hasLodging,
                    activity: currentPackage.activity,
                    containerRef: formFieldWrapperRef,
                  }}
                  timeSlots={updatedTimeSlots}
                  label={
                    datesChanged ? (
                      <FormattedMessage id="BookingChanges.newDates" />
                    ) : (
                      <FormattedMessage id="BookingChanges.originalDates" />
                    )
                  }
                  text={bookingDateText}
                  onChange={dates => {
                    setNewBookingDates(dates);
                    form.change('dates', dates);
                  }}
                  isPackage
                  onTooltipOpenChange={onDatePickerOpenChange}
                />
              )}

              {datesChanged && (
                <div className={css.formFieldHelper}>
                  <FormattedMessage id="BookingChanges.original" /> {bookingDatesText}
                </div>
              )}
            </div>

            <div className={css.priceDetailsMobile}>{pricingDetails}</div>

            {renderedInlinePayment && (
              <div className={css.formFieldWrapper}>{renderedInlinePayment}</div>
            )}

            <div className={css.formFieldWrapper}>
              {errorMessage && (
                <div id="error-message" className={css.errorMessage}>
                  {errorMessage}
                </div>
              )}

              <div className={css.formFieldButtonWrapper}>
                {hasCustomerRequestedChanges || isProvider ? (
                  <>
                    {isProvider ? (
                      <>
                        <PrimaryButton
                          className={css.acceptButton}
                          inProgress={acceptChangesInProgress}
                          type="button"
                          onClick={() => {
                            onAccept();
                          }}
                        >
                          <FormattedMessage id="BookingChanges.acceptButton" />
                        </PrimaryButton>
                        <SecondaryButton
                          inProgress={declineChangesInProgress}
                          type="button"
                          onClick={() => {
                            onDecline();
                          }}
                        >
                          <FormattedMessage id="BookingChanges.declineButton" />
                        </SecondaryButton>
                      </>
                    ) : (
                      <>
                        <PrimaryButton
                          type="button"
                          isFullWidth
                          onClick={() => {
                            onClose();
                          }}
                        >
                          <FormattedMessage id="BookingChanges.returnToBookingButton" />
                        </PrimaryButton>
                      </>
                    )}
                  </>
                ) : renderedInlinePayment ? null : (
                  <PrimaryButton
                    type="submit"
                    isFullWidth
                    inProgress={inProgress}
                    disabled={primaryActionDisabled}
                  >
                    <FormattedMessage id="BookingChanges.sendRequest" />
                  </PrimaryButton>
                )}
              </div>
            </div>
          </Form>
        );
      }}
    />
  );
};

export default BookingDetails;
